import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, ShieldAlert } from 'lucide-react';
import { acquireScrollLock, releaseScrollLock } from '@/lib/scroll-lock';
import { EnvironmentBadge } from './EnvironmentBadge';
import { useIsActingAsSystemAdmin } from '@/hooks/useIsActingAsSystemAdmin';
import { useActiveTenantDisplayName } from '@/hooks/useActiveTenantDisplayName';
import { registerModal, unregisterModal, isTopmostModal, resolveTopmostModal } from '@/lib/modal-stack';

export type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  /** フッタ（ボタン群）。省略時はフッタなし */
  footer?: ReactNode;
  /** オーバーレイクリックで閉じる（デフォルトtrue、破壊的操作の確認では false 推奨） */
  closeOnOverlay?: boolean;
  /** オーバーレイ+ダイアログのz-index Tailwindクラス (docs/design/ui-guidelines.md §4 z-indexスケール)。
   *  省略時は modal 層 (z-50)。ノード移動確認等、tree-popover(z-55)より上に出す必要がある場合に上書きする。 */
  zIndexClass?: string;
  /** ダイアログのARIA role。省略時は'dialog'。影響範囲の大きい確認 (業態変更等) では
   *  'alertdialog' を指定するとスクリーンリーダーが割り込みで読み上げる (v0.3 Design C-1) */
  role?: 'dialog' | 'alertdialog';
  /** 'right' で画面右端からスライドインするパネル (詳細ドロワー用)。'bottom-sheet' で画面下端から
   *  スライドインするシート (D-1010、767px以下のスマホ導線用)。省略時は従来の中央配置。
   *  'bottom-sheet' では size は無視され全幅になる。'right' では size がそのまま幅に反映される
   *  (§8-XXX是正: 以前は 'right' で size を無視し常に480px固定だったため、列数の多い表を持つ
   *  ドロワー(AttendanceReviewPage.tsx の月次明細)で `size="lg"` を渡しても幅が変わらず、
   *  横スクロールバーが表の最下部にしか現れず気づかれにくい状態になっていた)。 */
  position?: 'center' | 'right' | 'bottom-sheet';
};

const sizeCls: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
};

// 'right' Drawer 用の幅 (lg のみ lg breakpoint 以上で拡張し、768px 等の中間幅では既存の480pxを
// 維持する。既定値 'md' は既存呼び出し元(MonthlyAttendanceEntryPage.tsx・MemberDetailDrawer.tsx、
// どちらも size 未指定=既定'md')と完全に同じ挙動を保つ)。
const rightDrawerWidthCls: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:w-[360px]',
  md: 'sm:w-[480px]',
  lg: 'sm:w-[480px] lg:w-[820px]',
};

export function Modal({
  open, title, onClose, size = 'md', children, footer, closeOnOverlay = true, zIndexClass = 'z-50', role = 'dialog',
  position = 'center',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // 'right' Drawer のスライドイン用。初回描画は translate-x-full のまま行い、次フレームで
  // translate-x-0 に切り替えて transition を発火させる (マウント直後に class を変えないと
  // ブラウザが1フレーム目と2フレーム目を同一とみなしアニメーションしない)。
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) { setEntered(false); return; }
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);
  // ⚠️ TD-21: 入れ子モーダルで Escape / Tab を**最前面のモーダルだけ**が処理するために、
  // オーバーレイ要素を modal-stack へ登録して包含関係で最前面を判定する。
  const overlayRef = useRef<HTMLDivElement | null>(null);
  // onClose を ref に退避し、useEffect の依存から外して再実行を防ぐ
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    // 内部の要素が autoFocus 済みならそちらを優先（例：破壊的ダイアログの Cancel）。
    // それ以外の場合のみコンテナにフォーカス。
    const dialogEl = dialogRef.current;
    if (dialogEl && !dialogEl.contains(document.activeElement)) {
      dialogEl.focus();
    }
    const overlayEl = overlayRef.current;
    if (overlayEl) registerModal(overlayEl);
    const onKey = (e: KeyboardEvent) => {
      // ⚠️ TD-21: 最前面でなければ何もしない。これが無いと入れ子時に Escape で
      // 内側の確認ダイアログだけでなく外側の編集モーダルまで閉じてしまう。
      // ⚠️ test-runner Minor: `overlayEl &&` と書くと ref 未設定時に**旧バグ挙動へ無言で退行**する。
      // 最前面と確認できないなら処理しない (fail-safe) 方を既定にする。
      if (!overlayEl || !isTopmostModal(overlayEl)) return;
      // ⚠️ design-and-code HIGH: Radix の DismissableLayer は **capture 位相**で Escape を拾い、
      // 自分で閉じたときに `preventDefault()` する (@radix-ui/react-dismissable-layer)。
      // ここで defaultPrevented を見ないと、モーダル内で Select を開いて Escape したときに
      // **Select とモーダルが同時に閉じる** (「1段階だけ戻る」にならない)。
      // ⚠️ 自前のポップオーバー (NodeMultiSelect / StoreMultiSelect / InheritanceSourceBadge) は
      // preventDefault しないため、この行では守られない (個別対応が要る、OPEN_ITEMS に記録)。
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') onCloseRef.current();
      // 簡易 focus trap: ダイアログ内の focusable な要素間で Tab を循環させる
      if (e.key === 'Tab' && dialogEl) {
        const focusable = dialogEl.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        // ⚠️ design-expert Major-2: 端 (first/last) でしか折り返さない実装だと、フォーカスが
        // ダイアログの外にある状態 (入れ子で内側を開いた直後や、外側の要素へ抜けた後) では
        // どちらの分岐にも入らず**誰もトラップしない**。最前面の外に出ていたら引き戻す。
        // ⚠️ `inert` で背面を無効化する手は入れ子では使えない (外側に付けると DOM 子孫である
        // 内側まで無効化されるため)。ここで引き戻すのが入れ子・兄弟の双方に効く。
        if (active && !dialogEl.contains(active)) { first.focus(); e.preventDefault(); return; }
        if (e.shiftKey && active === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && active === last) { first.focus(); e.preventDefault(); }
      }
    };
    document.addEventListener('keydown', onKey);
    acquireScrollLock();
    return () => {
      document.removeEventListener('keydown', onKey);
      if (overlayEl) unregisterModal(overlayEl);
      releaseScrollLock();
      // ⚠️ design-expert Major-1: `prev` (開く前にフォーカスしていた要素) は、その要素自体が
      // 操作の結果 DOM から消えることがある (例: 一覧の「削除」ボタンから確認ダイアログを開き、
      // 削除が確定して行ごと消える)。その場合 focus() は no-op になり**フォーカスが body へ落ちて**
      // キーボード/SR 利用者が開いたままの外側モーダルの外に取り残される。
      if (prev?.isConnected) {
        prev.focus?.();
      } else {
        // まだ開いているモーダルがあればそこへ戻す (「1段階だけ戻る」ことを支援技術にも伝える)
        const top = resolveTopmostModal() as HTMLElement | null;
        top?.querySelector<HTMLElement>('[role="dialog"], [role="alertdialog"]')?.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  // Drawer (position='right') はオーバーレイを右端寄せ・余白ゼロにし、ダイアログ本体を
  // 固定幅の右端張り付きパネルにしてスライドインさせる (§3-3)。
  // bottom-sheet (D-1010) はオーバーレイを下端寄せ・余白ゼロにし、ダイアログ本体を全幅・下端張り付き
  // (角丸は上のみ、safe-area-inset-bottom分の余白込み) にして下からスライドインさせる。
  // (月次実績一括入力 第2フェーズ design-expert Critical反映: 独自実装の bottom sheet は
  // フォーカストラップ・Escape・初期フォーカス・フォーカス復帰が抜けやすいため、
  // Modal 本体が持つそれら一式をこの新バリアントとして共有する)。center 系は従来どおり無変更。
  const overlayPositionCls = position === 'right'
    ? 'items-stretch justify-end p-0'
    : position === 'bottom-sheet'
      ? 'items-end justify-center p-0'
      : 'items-center justify-center p-3 sm:p-4';
  const dialogPositionCls = position === 'right'
    ? `w-full ${rightDrawerWidthCls[size]} h-full rounded-l-xl rounded-r-none transition-transform duration-300 ease-out ${entered ? 'translate-x-0' : 'translate-x-full'}`
    : position === 'bottom-sheet'
      ? `w-full max-h-[90vh] rounded-t-xl rounded-b-none pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ease-out ${entered ? 'translate-y-0' : 'translate-y-full'}`
      : `w-full ${sizeCls[size]} max-h-[90vh] rounded-xl`;

  // ⚠️ [実装後レビューで発見・是正、design-expert Critical]: position='right' の Drawer は
  // translate-x-* (= CSS `transform`) でスライドインさせるが、`transform` が none 以外の要素は
  // 子孫の `position: fixed` の containing block になる (CSS Transforms仕様)。Drawer配下で
  // さらに Modal (position='center'、`fixed inset-0`) を開くと、そのオーバーレイが画面全体では
  // なくDrawerパネルの中に閉じ込められてしまう。`createPortal` で document.body 直下へ描画し、
  // 祖先の transform/overflow の影響を受けない構造にする (NodeMultiSelect.tsx 等と同じ既存パターン)。
  return createPortal(
    <div
      ref={overlayRef}
      className={`fixed inset-0 ${zIndexClass} flex ${overlayPositionCls} bg-black/40`}
      onMouseDown={(e) => {
        // ⚠️ design-expert Minor-2: Escape と同じく最前面だけが反応する。DOM 入れ子なら
        // `target === currentTarget` で偶然守られるが、兄弟配置では守られないため明示する。
        if (!overlayRef.current || !isTopmostModal(overlayRef.current)) return;
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`bg-surface-card shadow-overlay flex flex-col outline-none ${dialogPositionCls}`}
      >
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-surface-line">
          <h2 className="text-[17px] font-extrabold text-text-main tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-surface-sunken text-text-sub hover:text-text-main focus-ring"
            aria-label="閉じる"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {children}
        </div>
        {footer && (
          <div className="px-4 sm:px-5 py-3 border-t border-surface-line bg-surface-sunken flex flex-wrap justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// 確認ダイアログ（削除/危険な操作用）
export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** true の間は確認ボタンを無効化する (例: プレビュー未取得・対象0件など、実行してもno-opになる状態) */
  confirmDisabled?: boolean;
  /** ⚠️ 実行ボタンは従来 `destructive` の値に関わらず常に赤 (bg-brand-red) で固定されていた
   *  (docs/ui-guidelines.md §2.2 は赤を置いてよい場所を「破壊的操作の確認ダイアログ内の実行ボタン」
   *  に限定しており、非破壊的な確認 (取消・実行しても元に戻せる操作等) では赤にすべきでない)。
   *  月次実績一括入力 第2フェーズ 6並列レビュー (design-expert Major) 指摘で発見。既存の72箇所以上の
   *  呼び出し元は見た目維持のため既定値 'danger' のままとし、新規に非破壊的な用途で使う場合のみ
   *  呼び出し側で明示的に 'secondary' を指定する (オプトイン、既存呼び出し元への影響ゼロ)。 */
  confirmVariant?: 'danger' | 'secondary';
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
};

export function ConfirmDialog({
  open, title, message, confirmLabel = '実行', cancelLabel = 'キャンセル',
  destructive = false, confirmDisabled = false, confirmVariant = 'danger', onConfirm, onClose, loading = false,
}: ConfirmDialogProps) {
  // v1.0 Phase4 (docs/design/v10-phase4-platform-admin-ui.md §3-3): システム管理者が他テナントを
  // 操作中に破壊的操作を実行しようとした際、誤操作防止のため確認ダイアログ内に警告を追記する。
  // v1.0 Phase1-5最終監査 MEDIUM対応 (design書R-3残存リスク): 「他テナント」とだけ表示していたが
  // 具体的なテナント名を出すことで誤操作防止効果を高める
  const isActingAsSystemAdmin = useIsActingAsSystemAdmin();
  const tenantName = useActiveTenantDisplayName();
  return (
    <Modal
      open={open}
      title={title}
      size="sm"
      onClose={onClose}
      closeOnOverlay={!destructive}
      footer={
        <>
          {/* 破壊的操作では Cancel を初期フォーカスにして誤操作を防ぐ（autoFocus は Modal の focus trap で尊重される） */}
          <button type="button" onClick={onClose} disabled={loading}
            autoFocus={destructive}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken disabled:opacity-45 disabled:cursor-not-allowed focus-ring min-h-[44px] sm:min-h-[40px]">
            {cancelLabel}
          </button>
          <button type="button" onClick={onConfirm} disabled={loading || confirmDisabled}
            autoFocus={!destructive}
            className={`h-10 px-5 rounded-md text-[13px] font-extrabold disabled:opacity-45 disabled:cursor-not-allowed focus-ring min-h-[44px] sm:min-h-[40px] ${
              confirmVariant === 'secondary'
                ? 'bg-white text-text-body btn-press-secondary hover:bg-surface-sunken'
                : 'text-white btn-press-primary hover:bg-brand-red-hover bg-brand-red'
            }`}>
            {loading ? '処理中…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-text-body leading-relaxed">
        {destructive && <EnvironmentBadge variant="dialog" />}
        {destructive && isActingAsSystemAdmin && (
          <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5 rounded bg-danger-bg text-danger-text text-xs font-semibold">
            <ShieldAlert size={14} className="shrink-0" aria-hidden />
            「{tenantName ?? '他テナント'}」で実行しようとしています
          </div>
        )}
        {message}
      </div>
    </Modal>
  );
}
